<template>
  <div class="t-border t-border-gray-200 t-shadow-lg t-py-2 t-px-3 t-bg-white md:t-rounded-lg">
    <!-- Card Heading -->
    <div class="t-flex t-justify-between t-items-baseline t-py-2">
      <div class="t-flex t-space-x-2 t-items-center">
        <p class="t-text-2xl t-font-medium t-text-blue-500 hover:t-text-blue-900">
          <a :href="'/send-freight/bookings/' + booking.id">
            {{ booking.number }}
          </a>
        </p>
        <span
          v-if="statusHuman(booking.status_id) === 'BOOKED'"
          class="t-py t-px-2 t-rounded t-bg-orange t-text-white t-font-semibold"
        >
          BOOKED
        </span>
        <span
          v-if="statusHuman(booking.status_id) === 'IN TRANSIT'"
          class="t-py t-px-2 t-rounded t-bg-dodger-blue t-text-white t-font-semibold"
        >
          IN TRANSIT
        </span>
        <span
          v-if="statusHuman(booking.status_id) === 'CANCELLED'"
          class="t-py t-px-2 t-rounded t-bg-red-500 t-text-white t-font-semibold"
        >
          CANCELLED
        </span>
        <span
          v-if="statusHuman(booking.status_id) === 'DELIVERED'"
          class="t-py t-px-2 t-rounded t-bg-green-600 t-text-white t-font-semibold"
        >
          DELIVERED
        </span>
      </div>
    </div>

    <div class="t-border-t-2 t-border-b-2 t-border-gray-300 t-flex t-flex-col t-pb-2 md:t-flex-row md:t-justify-between md:t-pb-0">
      <div class="t-grid t-grid-rows-2 t-grid-cols-1 t-text-sm t-py-1 md:t-grid-rows-1 md:t-grid-cols-2 md:t-divide-x-2 md:t-divide-gray-300">
        <div class="t-px-2 t-py-1">
          <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
            Placed
          </p>
          <p>{{ booking.placed_at }}</p>
        </div>
        <div class="t-px-2 t-py-1">
          <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
            Pickup Carrier
          </p>
          <p>{{ booking.carrier }}</p>
        </div>
      </div>

      <div class="t-flex t-flex-col t-space-y-3 t-space-x-2 t-text-md md:t-block md:t-space-y-2 md:t-text-sm md:t-py-2">
        <a
          v-if="booking.carrier !== 'Aramex'"
          :href="'/send-freight/bookings/' + booking.id + '/all'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-transition t-duration-300 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          All Files
        </a>

        <a
          :href="'/send-freight/bookings/' + booking.id + '/address-label'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-transition t-duration-300 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          Address Label
        </a>

        <a
          :href="'/send-freight/bookings/' + booking.id + '/consignment'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-transition t-duration-300 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          Consignment
        </a>

        <a
          v-if="booking.is_dg > 0"
          :href="'/send-freight/bookings/' + booking.id + '/dgs'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-transition t-duration-300 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          DGs
        </a>

        <a
          v-if="booking.has_pod"
          :href="'/send-freight/bookings/' + booking.id + '/proof-of-delivery'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-transition t-duration-300 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          Proof of Delivery
        </a>
      </div>
    </div>

    <!-- Card Body -->
    <div class="t-grid t-grid-rows-3 t-grid-cols-1 t-gap-y-3 t-mt-2 t-px-2 md:t-grid-rows-1 md:t-grid-cols-12">
      <div class="md:t-col-span-3 t-text-sm">
        <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
          From
        </p>
        <p class="t-font-semibold">{{ booking.pickup_location.name }}</p>
        <p>{{ booking.pickup_location.street }}</p>
        <p>{{ booking.pickup_location.suburb }}</p>
        <p>{{ booking.pickup_location.district }}</p>

        <p class="t-font-medium t-mt-2">
          Pickup After: <span class="t-font-normal">{{ booking.pickup_at }}</span>
        </p>
        <p
          v-if="booking.pickupMovement"
          class="t-font-medium"
        >
          Picked Up At: <span class="t-font-normal">{{ booking.pickupMovement.data.time }}</span>
        </p>
      </div>
      <div class="md:t-col-span-3 t-text-sm">
        <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
          To
        </p>
        <p class="t-font-semibold">{{ booking.deliver_location.name }}</p>
        <p>{{ booking.deliver_location.street }}</p>
        <p>{{ booking.deliver_location.suburb }}</p>
        <p>{{ booking.deliver_location.district }}</p>

        <p class="t-font-medium t-mt-2">
          Deliver By: <span class="t-font-normal">{{ booking.deliver_at }} </span>
        </p>
      </div>
      <div class="md:t-col-span-6">
        <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold t-border-b-2 t-border-gray-300 t-pb-2 t-mb-2">
          Freight
        </p>

        <div
          class="t-grid t-grid-cols-5 t-grid-rows-1 t-gap-x-2"
          v-for="freight in booking.freights"
          :key="freight.id"
        >
          <p class="t-col-span-3">
            {{ freight.quantity }} x {{ freight.package_type }} {{ freight.goods_type }}
          </p>
          <p>
            {{ freight.weight }} kg
          </p>
          <p>
            {{ freight.volume }} m<sup>3</sup>
          </p>
        </div>

        <div class="t-grid t-grid-cols-5 t-grid-rows-1 t-font-medium t-mt-1 t-border-t t-border-gray-300 t-gap-x-2">
          <p class="t-col-span-3">Total</p>
          <p>{{ booking.weight }} kg</p>
          <p>{{ booking.volume }} m<sup>3</sup></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NetworkBookingCard',
  description: '',
  props: {
    booking: {
      type: Object, required: true
    },
    pickedUpChecked: {
      type: Boolean
    },
    bookingStatus: {
      type: String, default: null
    }
  },
  methods: {
    statusHuman(statusId) {
      if (statusId === 2 || statusId === 3) {
        return 'BOOKED';
      } else if (statusId === 4) {
        return 'IN TRANSIT';
      } else if (statusId === 5) {
        return 'DELIVERED';
      } else if (statusId === 9) {
        return 'CANCELLED';
      } else {
        return '-';
      }
    }
  }
}
</script>

<style scoped>
a {
  text-decoration: none;
}
</style>
